import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { teacherModuleAPI } from '../../services/api';
import StudentAssessmentEditor, {
  buildAllModuleStates,
  moduleHasData,
  modulesForSave,
  flattenAssessmentCatalog,
} from '../../components/Teacher/StudentAssessmentEditor';

const STATUS_LABELS = {
  not_started: 'Not started',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Needs revision',
};

const STATUS_COLOR = {
  draft: 'warning',
  submitted: 'info',
  approved: 'success',
  rejected: 'error',
};

function formatMonthLabel(month) {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

export default function StudentMonthlyReportForm() {
  const { studentId, month } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modules, setModules] = useState([]);
  const [overallRemarks, setOverallRemarks] = useState('');
  const [initialized, setInitialized] = useState(false);

  const detailQuery = useQuery(
    ['studentMonthlyReportDetail', studentId, month],
    async () => {
      const [detailRes, catalogRes] = await Promise.all([
        teacherModuleAPI.getStudentMonthlyReport(studentId, month),
        teacherModuleAPI.getStudentAssessmentCatalog(),
      ]);
      return {
        detail: detailRes.data?.data,
        catalog: catalogRes.data?.data || [],
      };
    }
  );

  const catalog = useMemo(
    () => detailQuery.data?.catalog || [],
    [detailQuery.data?.catalog]
  );
  const detail = detailQuery.data?.detail;
  const report = detail?.report;
  const statusLocked = report && !['draft', 'rejected'].includes(report.status);
  const readOnly =
    detail?.isInherited === true ||
    detail?.readOnly === true ||
    (detail?.readOnly !== false && statusLocked);
  const teachingHint = detail?.teachingHint;

  const flatModules = useMemo(() => flattenAssessmentCatalog(catalog), [catalog]);

  useEffect(() => {
    if (!flatModules.length || initialized) return;
    setModules(buildAllModuleStates(flatModules, report?.assessmentData));
    setOverallRemarks(report?.assessmentData?.overallRemarks || '');
    setInitialized(true);
  }, [flatModules, report, initialized]);

  const updateSelection = (moduleId, parameterId, optionId) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.moduleId !== moduleId) return m;
        return {
          ...m,
          selections: m.selections.map((s) =>
            s.parameterId === parameterId ? { ...s, optionId } : s
          ),
        };
      })
    );
  };

  const updateModuleRemarks = (moduleId, remarks) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.moduleId !== moduleId) return m;
        return {
          ...m,
          remarks,
          sendCustomNotesOnly: remarks.trim() ? Boolean(m.sendCustomNotesOnly) : false,
        };
      })
    );
  };

  const updateSendCustomNotesOnly = (moduleId, sendCustomNotesOnly) => {
    setModules((prev) =>
      prev.map((m) => (m.moduleId === moduleId ? { ...m, sendCustomNotesOnly } : m))
    );
  };

  const payloadModules = useMemo(() => modulesForSave(modules), [modules]);

  const saveMutation = useMutation(
    () =>
      teacherModuleAPI.saveStudentMonthlyReport(studentId, month, {
        batchId: detail?.student?.batchId,
        remarks: overallRemarks,
        assessmentData: { modules: payloadModules, overallRemarks },
      }),
    {
      onSuccess: () => {
        toast.success('Saved');
        queryClient.invalidateQueries(['studentMonthlyReportDetail', studentId, month]);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Save failed'),
    }
  );

  const submitMutation = useMutation(
    () => teacherModuleAPI.submitStudentMonthlyReport(studentId, month),
    {
      onSuccess: () => {
        toast.success('Submitted for supervisor review');
        queryClient.invalidateQueries(['studentMonthlyReports']);
        navigate('/teacher/student-reports');
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Submit failed'),
    }
  );

  const handleSubmit = async () => {
    const started = modules.filter((m) => moduleHasData(m));
    if (started.length === 0) {
      toast.error('Fill at least one row before submitting');
      return;
    }
    await saveMutation.mutateAsync();
    submitMutation.mutate();
  };

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (detailQuery.isError) {
    const errMessage =
      detailQuery.error?.response?.data?.message ||
      detailQuery.error?.message ||
      'Student report not available';
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/teacher/student-reports')}
          sx={{ mb: 2 }}
        >
          Back to student list
        </Button>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errMessage}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Only students with at least one present or makeup attendance in your class for this month
          appear in the list and can have an assessment filled.
        </Typography>
      </Box>
    );
  }

  const student = detail?.student;
  const studentName = `${student?.firstName || ''} ${student?.lastName || ''}`.trim();
  const monthLabel = formatMonthLabel(month);
  const status = report?.status || 'not_started';

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 10, md: 3 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/teacher/student-reports')}
        sx={{ mb: 2 }}
      >
        Back to student list
      </Button>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        mb={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {studentName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {monthLabel}
            {student?.batchName ? ` · Batch: ${student.batchName}` : ''}
            {student?.displayId ? ` · ${student.displayId}` : ''}
          </Typography>
        </Box>
        <Chip
          label={STATUS_LABELS[status] || status}
          color={STATUS_COLOR[status] || 'default'}
          size="small"
        />
      </Stack>

      {detail?.isInherited && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This report was filed by {detail.reportingTeacherName || 'a previous teacher'} when the
          student was in another batch. It is read-only for you.
        </Alert>
      )}
      {report?.status === 'rejected' && !detail?.isInherited && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Supervisor feedback: {report.rejectionReason}
        </Alert>
      )}
      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This report is {report.status} — view only.
        </Alert>
      )}
      {!readOnly && report?.status === 'submitted' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Approval was cancelled. Update the assessment and save. It stays in the supervisor inbox.
        </Alert>
      )}

      {!readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Fill only what you assessed this month — any module or parameter is fine; leave the rest
          blank. You can submit without completing every row in a module.
          {teachingHint?.sessionsAttended != null && (
            <>
              {' '}
              This student attended {teachingHint.sessionsAttended} session(s) this month.
            </>
          )}
        </Alert>
      )}

      <StudentAssessmentEditor
        catalog={catalog}
        modules={modules}
        overallRemarks={overallRemarks}
        readOnly={readOnly}
        onChangeSelection={updateSelection}
        onChangeModuleRemarks={updateModuleRemarks}
        onChangeSendCustomNotesOnly={updateSendCustomNotesOnly}
        onChangeOverallRemarks={setOverallRemarks}
      />

      {!readOnly && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="large"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isLoading}
          >
            Save
          </Button>
          {(!report || ['draft', 'rejected'].includes(report.status)) && (
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitMutation.isLoading}
            >
              Submit for review
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
}
